import { ExtraCollaboratorResponse } from '@/app/models/extraCollaborator';
import { useRemoveCollaboratorExtraMutation } from '@/app/services/extraCollaboratorApi';
import ActionMenu, { ItemAction } from '@/components/common/ActionMenu';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import CancelOrConfirmDialog from '@/components/common/dialogs/CancelOrConfirmDialog';
import { TableCell, useTheme } from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type ExtraCollaboratorParams = {
  rows: ExtraCollaboratorResponse[];
  setSelectedItem: (p: ExtraCollaboratorResponse) => void;
  setDrawerOpen: (open: boolean) => void;
};

export default function ExtraCollaboratorTable(props: ExtraCollaboratorParams) {
  const theme = useTheme();
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);

  const { t } = useTranslation(['extraCollaborator', 'common']);
  const { rows, setSelectedItem, setDrawerOpen } = props;

  const [deleteExtraCollaborator] = useRemoveCollaboratorExtraMutation();

  const handleDialogClose = () => {
    setDeleteDialog(false);
  };

  const handleDeleteItem = async () => {
    if (selectedId) {
      const response = await deleteExtraCollaborator(selectedId);
      if ('data' in response) {
        toast.dismiss();
        toast.success(
          t('historic.collaborator.priority.set.success', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      } else {
        toast.error(
          t('common:processing.failed', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      }
      handleDialogClose();
      setSelectedId(null);
    }
  };

  const columns: Column[] = [
    { key: 'collaborator', label: t('common:collaborator'), width: theme.spacing(80) },
    { key: 'priority', label: t('common:priority'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (row: ExtraCollaboratorResponse) => (
    <>
      <TableCell sx={bodyCellSx}>{`${row.person.badge} - ${row.person.name}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{`${row.priority}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <ActionMenu>
          <ItemAction
            text={t('common:edit') as string}
            actionType="edit"
            onClick={() => {
              setSelectedItem(row);
              setDrawerOpen(true);
            }}
          />
          <ItemAction
            text={t('common:delete') as string}
            actionType="delete"
            onClick={() => {
              setSelectedId(row.id);
              setDeleteDialog(true);
            }}
          />
        </ActionMenu>
      </TableCell>
    </>
  );

  // Use the row ID as the unique key
  const getRowKey = (row: ExtraCollaboratorResponse) => row.id;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: rows?.length || 0,
    rowsPerPageOptions: [25, 50, 100, 150, 200],
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('de')} ${count}`,
    labelRowsPerPage: t('common:itens.per.page'),
  };

  const deleteDialogProps = {
    title: t('common:confirm.delete.title'),
    contentText: <Trans>{t('common:confirm.delete.message')}</Trans>,
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        stickyHeader
        size="small"
      />

      <CancelOrConfirmDialog
        title={deleteDialogProps.title}
        contentText={deleteDialogProps.contentText}
        open={deleteDialog}
        onClose={() => handleDialogClose()}
        onConfirm={async () => handleDeleteItem()}
      />
    </>
  );
}
